import React from 'react';
import {useTranslation} from 'react-i18next';
import {Box, Flex, Heading} from '@chakra-ui/react';
import {HookUseTypedFilters} from '@synako/typed-path';
import {HaliRenderOne} from '@synako/haligator';
import {CategoryFilterButton} from '../../catalogueGrandCompte/components/CategoryFilterButton';
import {ChevronLeftIcon} from '@chakra-ui/icons';
import {capitalizeFirstLetter} from 'ff-common';
import {FiltresItemCatalogue} from 'ff-common/src/hooks/search/use-search';
import {Results} from '@orama/orama';
import {HitItemCatalogue} from 'api-types/ffconnect';
import {State} from '@synako/halitrek';
import {MarketplaceDesc} from 'api-types/ffconnect-hal';
import {useMarketplaceBrandColors} from '@/hooks/marketplace/useMarketplaceBrandColors';

interface MarketplaceCategoryFiltersBarProps {
  filtersHook: HookUseTypedFilters<FiltresItemCatalogue>;
  facets: Results<HitItemCatalogue>['facets'];
  marketplaceState: State<MarketplaceDesc>;
}

export const MarketplaceCategoryFiltersBar: React.FC<
  MarketplaceCategoryFiltersBarProps
> = ({filtersHook, facets, marketplaceState}) => {
  const {t} = useTranslation();
  const {couleur, textCouleur} = useMarketplaceBrandColors(
    marketplaceState.data.marketplace_id,
  );
  const primaryColor = couleur ?? 'categories.frais';
  const primaryTextColor = textCouleur ?? 'gray.dark';
  const currentCategoryId = filtersHook.params.cat_marketplace?.[0];
  const categoryIdsWithResults = facets?.['cat_marketplace']?.values
    ? new Set(Object.keys(facets['cat_marketplace'].values))
    : null;

  const handleFilterClick = (categoryId: string) => {
    filtersHook.change('cat_marketplace', categoryId);
  };

  const nomenclatureNav = marketplaceState.follow('nomenclature');

  return (
    <HaliRenderOne
      nav={nomenclatureNav}
      render={({data: dataNomenclature}) => {
        const items = dataNomenclature.items;

        const currentCategory = currentCategoryId
          ? items.find((cat) => cat.id === currentCategoryId)
          : null;

        const parentCategory = currentCategory?.id_parent
          ? (items.find((cat) => cat.id === currentCategory.id_parent) ?? null)
          : null;

        const parentCategories = items.filter((cat) => !cat.id_parent);
        const childCategories = currentCategoryId
          ? items.filter((cat) => cat.id_parent === currentCategoryId)
          : [];

        const displayCategoriesRaw =
          !currentCategoryId || currentCategoryId.length === 0
            ? parentCategories
            : childCategories;
        const displayCategories = categoryIdsWithResults
          ? displayCategoriesRaw.filter((cat) =>
              categoryIdsWithResults.has(cat.id),
            )
          : displayCategoriesRaw;

        const hasCategoryToShow =
          displayCategories.length > 0 || currentCategory;
        if (!hasCategoryToShow) {
          return null;
        }

        const headingText = currentCategory
          ? capitalizeFirstLetter(currentCategory.libelle)
          : t('marketplace.titre_catalogue', {nom: marketplaceState.data.nom});

        return (
          <Box width='100%'>
            <Heading
              as='h2'
              size='lg'
              color={primaryTextColor}
              pt={{base: 3, md: 4}}
              pb={1}
            >
              {headingText}
            </Heading>
            <Flex
              flexWrap='wrap'
              gap={{base: 1.5, sm: 2, md: 2}}
              py={3}
              pb={{base: 3, md: 4}}
              pt={{base: 3, md: 4}}
            >
              {currentCategory && (
                <CategoryFilterButton
                  label={
                    parentCategory
                      ? capitalizeFirstLetter(parentCategory.libelle)
                      : t('grand_compte.categories')
                  }
                  isActive={true}
                  leftIcon={<ChevronLeftIcon />}
                  onClick={() => {
                    if (parentCategory) {
                      handleFilterClick(parentCategory.id);
                    } else {
                      filtersHook.change('cat_marketplace', undefined);
                    }
                  }}
                  primaryColor={primaryColor}
                  primaryTextColor={primaryTextColor}
                />
              )}

              {displayCategories.map((cat) => (
                <CategoryFilterButton
                  key={cat.id}
                  label={capitalizeFirstLetter(cat.libelle)}
                  isActive={
                    filtersHook.params.cat_marketplace?.includes(cat.id) ??
                    false
                  }
                  onClick={() => {
                    handleFilterClick(cat.id);
                  }}
                  primaryColor={primaryColor}
                  primaryTextColor={primaryTextColor}
                />
              ))}
            </Flex>
          </Box>
        );
      }}
    />
  );
};
